// biome-ignore lint/style/useNodejsImportProtocol: we use the events module, but biome can't detect it in a monorepo yet
import Path from 'path';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getStorageCachedURL, isImageFile } from '@gitbook/app-database';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import {
    createPalette,
    DateShortRelative,
    TextMatchHighlight,
    usePaletteTrigger,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    DialogRoot,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { assertIsDefined, filterOutNullable } from '@gitbook/util-ts';

import { useLoggedInUser } from '../../state';
import { getHumanFileSize } from '../../utils';
import { useMediaGallery } from '../MediaGallery';
import { UserPrettyName } from '../Users';
import { DeleteFileDialog } from './DeleteFileDialog';
import { FileIcon } from './FileIcon';
import { ImageGridItem } from './ImagesGrid';
import { RenameFileDialog } from './RenameFileDialog';
import { useUploadFiles } from './useUploadFiles';

export type FileItemMode = 'list' | 'grid';

/**
 * Entry in the list of a file.
 */
export function FileItem(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    /** File to display */
    file: hiveCore.RevisionFile;
    /** If true, no edit actions are displayed */
    readOnly: boolean;
    /** Mode to render the file in */
    mode: FileItemMode;
    /** Search query used to filter the list */
    query: string;
    /** Callback when the user is pressing the file */
    onPress?: () => void;
}): React.ReactElement {
    const { space, changeRequest, readOnly, mode, file, query, onPress } = props;

    const user = useLoggedInUser();
    const mediaGallery = useMediaGallery();
    const hiveBranch = useHiveBranch();
    const deleteDialogTrigger = useDialogTriggerState();
    const renameDialogTrigger = useDialogTriggerState();
    const { promptAndUpload } = useUploadFiles();

    const lastEdit = hiveCore.getLastEdit(file?.edits || {});
    const downloadURL = getStorageCachedURL(file.downloadURL);

    const paletteTrigger = usePaletteTrigger({
        palette: () => {
            const replace = async () => {
                assertIsDefined(hiveBranch);

                const metadata = await promptAndUpload({ multiple: false });
                if (!metadata) {
                    return;
                }

                analytics.logEvent(
                    analytics.createSpaceEvent(space, {
                        type: 'edit_replace_file',
                    })
                );

                await hiveBranch.change((change) => {
                    change.updateFile(file.id, metadata[0]);
                    change.markFileAsEdited(file.id, user.uid);
                });
            };

            return createPalette({
                groups: [
                    {
                        key: 'read',
                        commands: [
                            isImageFile(file)
                                ? {
                                      icon: Icon.Maximize,
                                      key: 'file.preview',
                                      title: 'Quick look',
                                      trigger: () => {
                                          mediaGallery.open([
                                              {
                                                  url: downloadURL,
                                              },
                                          ]);
                                      },
                                  }
                                : null,
                            {
                                icon: Icon.Download,
                                key: 'file.download',
                                title: 'Download',
                                trigger: () => {
                                    analytics.logEvent(
                                        analytics.createSpaceEvent(space, {
                                            type: 'edit_download_file',
                                        })
                                    );

                                    if (downloadURL) {
                                        window.open(downloadURL, '_blank');
                                    }
                                },
                            },
                        ].filter(filterOutNullable),
                    },
                    readOnly
                        ? null
                        : {
                              key: 'edit',
                              commands: [
                                  {
                                      icon: Icon.Replace,
                                      key: 'file.replace',
                                      title: 'Replace',
                                      trigger: () => {
                                          void replace();
                                      },
                                  },
                                  {
                                      icon: Icon.Edit,
                                      key: 'file.edit',
                                      title: 'Edit',
                                      trigger: () => {
                                          renameDialogTrigger.open();
                                      },
                                  },
                                  {
                                      icon: Icon.Trashcan,
                                      key: 'file.delete',
                                      title: 'Delete',
                                      trigger: () => {
                                          deleteDialogTrigger.open();
                                      },
                                  },
                              ],
                          },
                ].filter(filterOutNullable),
            });
        },
    });

    return (
        <>
            {mode === 'list' ? (
                <ListItem
                    selected={paletteTrigger.opened}
                    onPress={onPress ?? paletteTrigger.triggerProps.onPress}
                >
                    <ListItemIndicator>
                        <FileIcon file={file} zoomable size={32} />
                    </ListItemIndicator>
                    <ListItemBody>
                        <ListItemDescription>
                            <TextMatchHighlight query={query} text={file.name} />
                        </ListItemDescription>
                        <ListItemDetail>
                            {lastEdit ? (
                                <React.Suspense fallback={null}>
                                    <UserPrettyName userId={lastEdit.user} />
                                    {' edited '}
                                    <DateShortRelative
                                        value={new Date(lastEdit.updatedAt)}
                                        onlyDate={false}
                                    />
                                    {' • '}
                                </React.Suspense>
                            ) : null}
                            {`${getHumanFileSize(file.size)} • ${Path.extname(file.name)
                                .slice(1)
                                .toUpperCase()}`}
                        </ListItemDetail>
                    </ListItemBody>
                    <ListItemControls>
                        <Button
                            {...paletteTrigger.triggerProps}
                            leadingIcon={Icon.MoreVertical}
                            label="More"
                            size="small"
                            kind="nested"
                            iconOnly
                        />
                    </ListItemControls>
                </ListItem>
            ) : (
                <ImageGridItem
                    src={downloadURL}
                    onPress={onPress ?? paletteTrigger.triggerProps.onPress}
                    selected={paletteTrigger.opened}
                    button={
                        <Button
                            {...paletteTrigger.triggerProps}
                            leadingIcon={Icon.MoreVertical}
                            label="More"
                            size="small"
                            kind="secondary"
                            iconOnly
                        />
                    }
                >
                    <TextMatchHighlight query={query} text={file.name} />
                </ImageGridItem>
            )}

            <DialogRoot {...deleteDialogTrigger.rootProps}>
                <DeleteFileDialog
                    space={space}
                    changeRequest={changeRequest}
                    file={file}
                    onClose={deleteDialogTrigger.close}
                />
            </DialogRoot>

            <DialogRoot {...renameDialogTrigger.rootProps}>
                <RenameFileDialog space={space} file={file} />
            </DialogRoot>
        </>
    );
}
